/**
 * Goals List Page — KPI summary + progress tracking.
 *
 * Shows 4 KPI counts (On Track / At Risk / Behind / Completed) and a
 * list of goals with inline progress bars. Uses Refine useList + BLL routes.
 */

import * as React from 'react';
import { useList, useUpdate, useDelete } from '@refinedev/core';
import { useNavigate } from 'react-router';
import {
  KpiCard,
  StatusBadge,
  EmptyState,
  Button,
  Card,
  CardContent,
  Badge,
} from '@yesbos/ui';
import { Target, Plus, TrendingUp, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

interface Goal {
  id: string;
  title: string;
  description?: string;
  progress: number;   // 0–100
  status: 'on_track' | 'at_risk' | 'behind' | 'completed' | 'draft';
  due_date?: string;
  employee_id?: string;
  employee_name?: string;
  category?: string;
}

// ── Status config ────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<Goal['status'], 'success' | 'warning' | 'destructive' | 'secondary' | 'primary'> = {
  on_track: 'success',
  at_risk: 'warning',
  behind: 'destructive',
  completed: 'primary',
  draft: 'secondary',
};

const STATUS_LABEL: Record<Goal['status'], string> = {
  on_track: 'On Track',
  at_risk: 'At Risk',
  behind: 'Behind',
  completed: 'Completed',
  draft: 'Draft',
};

// ── Goal Card ────────────────────────────────────────────────────────────────

function GoalCard({ goal, onUpdateProgress, onDelete }: { goal: Goal; onUpdateProgress: (id: string, progress: number) => void; onDelete: (id: string, title: string) => void }) {
  const navigate = useNavigate();
  const [editingProgress, setEditingProgress] = React.useState(false);
  const [localProgress, setLocalProgress] = React.useState(goal.progress);
  const [saving, setSaving] = React.useState(false);

  async function handleSaveProgress() {
    setSaving(true);
    await onUpdateProgress(goal.id, localProgress);
    setSaving(false);
    setEditingProgress(false);
  }

  const progressColor = goal.progress >= 75 ? 'bg-green-500' : goal.progress >= 40 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <p
              className="font-medium text-foreground cursor-pointer hover:text-primary truncate"
              onClick={() => navigate(`/goals/${goal.id}`)}
            >
              {goal.title}
            </p>
            {goal.description && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{goal.description}</p>
            )}
          </div>
          <StatusBadge status={goal.status} />
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Progress</span>
            {editingProgress ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={localProgress}
                  onChange={(e) => setLocalProgress(Math.min(100, Math.max(0, Number(e.target.value))))}
                  className="w-14 border rounded px-1.5 py-0.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="text-muted-foreground">%</span>
                <button
                  onClick={handleSaveProgress}
                  disabled={saving}
                  className="text-xs text-primary hover:underline disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => { setEditingProgress(false); setLocalProgress(goal.progress); }}
                  className="text-xs text-muted-foreground hover:underline"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditingProgress(true)}
                className="text-xs font-medium hover:text-primary"
              >
                {goal.progress}%
              </button>
            )}
          </div>
          <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${progressColor}`}
              style={{ width: `${goal.progress}%` }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 mt-3">
          {goal.category && (
            <Badge variant="outline" className="text-xs">{goal.category}</Badge>
          )}
          {goal.due_date && (
            <span className="text-xs text-muted-foreground">
              Due {new Date(goal.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => navigate(`/goals/${goal.id}/edit`)}
              className="text-xs text-muted-foreground hover:text-foreground px-1"
              title="Edit"
            >Edit</button>
            <button
              onClick={() => onDelete(goal.id, goal.title)}
              className="text-xs text-destructive hover:underline px-1"
              title="Delete"
            >Delete</button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function GoalsListPage() {
  const navigate = useNavigate();
  const { mutate: update } = useUpdate();
  const { mutate: deleteGoal } = useDelete();

  const { data, isLoading, refetch } = useList<Goal>({
    resource: 'goals',
    pagination: { pageSize: 100 },
  });

  const goals = (data?.data ?? []) as Goal[];

  // KPI counts
  const counts = React.useMemo(() => ({
    on_track: goals.filter((g) => g.status === 'on_track').length,
    at_risk: goals.filter((g) => g.status === 'at_risk').length,
    behind: goals.filter((g) => g.status === 'behind').length,
    completed: goals.filter((g) => g.status === 'completed').length,
  }), [goals]);

  function handleDeleteGoal(id: string, title: string) {
    if (!window.confirm(`Delete goal "${title}"? This cannot be undone.`)) return;
    deleteGoal({ resource: 'goals', id }, { onSuccess: () => refetch() });
  }

  async function handleUpdateProgress(id: string, progress: number) {
    const status: Goal['status'] = progress === 100 ? 'completed' : progress >= 70 ? 'on_track' : progress >= 40 ? 'at_risk' : 'behind';
    await new Promise<void>((resolve, reject) => {
      update(
        { resource: 'goals', id, values: { progress, status } },
        { onSuccess: () => { refetch(); resolve(); }, onError: () => reject() },
      );
    });
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Goals</h1>
        <Button onClick={() => navigate('/goals/create')}>
          <Plus className="h-4 w-4 mr-2" /> Add Goal
        </Button>
      </div>

      {/* KPI Row */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <KpiCard
          title="On Track"
          value={counts.on_track}
          icon={<TrendingUp className="h-5 w-5" />}
          color="success"
        />
        <KpiCard
          title="At Risk"
          value={counts.at_risk}
          icon={<AlertTriangle className="h-5 w-5" />}
          color="warning"
        />
        <KpiCard
          title="Behind"
          value={counts.behind}
          icon={<Clock className="h-5 w-5" />}
          color="destructive"
        />
        <KpiCard
          title="Completed"
          value={counts.completed}
          icon={<CheckCircle2 className="h-5 w-5" />}
          color="primary"
        />
      </div>

      {/* Goals Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 rounded-lg border bg-muted animate-pulse" />
          ))}
        </div>
      ) : goals.length === 0 ? (
        <EmptyState
          icon={<Target className="h-8 w-8" />}
          title="No Goals Yet"
          description="Set your first goal to start tracking progress."
          action={{ label: 'Add Goal', onClick: () => navigate('/goals/create') }}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {goals.map((goal) => (
            <GoalCard key={goal.id} goal={goal} onUpdateProgress={handleUpdateProgress} onDelete={handleDeleteGoal} />
          ))}
        </div>
      )}
    </div>
  );
}

export default GoalsListPage;
